/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Model } from '@app/shared/models/model.model';
import { Room } from '@app/hotels/rooms/room.model';
import { Events } from '@app/events/events/events.model';
import { Hotel } from '@app/hotels/hotels/hotel.model';
import { Supplier } from '@app/suppliers/suppliers/suppliers.model';
import { Currency } from '@app/settings/currencies/currency.model';
import { Company } from '@app/settings/companies/company.model';
import { Night } from '@app/hotels/rooms/night.model';
import { Type } from '@app/settings/types/types.model';
import { User } from '@app/settings/users/user.model';
import { sumBy, unionBy } from 'lodash';

export class HotelBooking extends Model {
  id: number;
  hotel_id: number;
  hotel?: Hotel;
  supplier_id?: number;
  supplier?: Supplier;
  company_id: number;
  company?: Company;
  commission_percent: number;
  deposit_percent: number;
  cancellation_charge_percent: number;
  ref_number: string;
  cancellation_deadline: string;
  payment_deadline: string;
  currency_id: number;
  currency?: Currency;
  show_to_web: boolean;
  events: Array<Events>;
  rooms: Array<Room>;
  note: string;
  breakfast: boolean;
  property_id: number;
  property?: Type;
  contracting_agent_id: number;
  contracting_agent?: User;
  reservation_agent_id: number;
  reservation_agent?: User;
  amendment_agent_id: number;
  amendment_agent?: User;
  cancellation_terms_id: number;
  cancellation_terms?: Type;
  total_amount: number;
  balance: number;
  breakfast_price_per_night: number;
  breakfast_price_per_night_currency_id: number;
  breakfast_price_per_night_currency: Currency;
  available_nights_count?: number;
  cancellations_count?: number;
  tags: Array<string>;
  exchange_rate: number;

  assign_to_user_id: number;
  assign_to_role_id: number;
  assign_to_user?: User;
  assign_date: string;
  book: boolean;

  deserialize(input: any): this {
    super.deserialize(input);

    if (input.events) {
      this.events = input.events.map((event: Events) => new Events().deserialize(event));
    }

    if (input.property) {
      this.property = new Type().deserialize(input.property);
    }

    if (input.cancellation_terms) {
      this.cancellation_terms = new Type().deserialize(input.cancellation_terms);
    }

    if (input.company) {
      this.company = new Company().deserialize(input.company);
    }

    if (input.contracting_agent) {
      this.contracting_agent = new User().deserialize(input.contracting_agent);
    }

    if (input.reservation_agent) {
      this.reservation_agent = new User().deserialize(input.reservation_agent);
    }

    if (input.amendment_agent) {
      this.amendment_agent = new User().deserialize(input.amendment_agent);
    }

    return this;
  }

  /**
   * Връща дали има поне един available Night в Cancellation-a
   */
  isSomeNightAvailable(): boolean {
    let someNightAvailable = false;
    this.rooms.forEach((rooom: Room) => {
      rooom.nights.forEach((night: Night) => {
        if (night.isNightAvailable()) {
          someNightAvailable = true;
        }
      });
    });
    return someNightAvailable;
  }

  get roomsRefNumber(): Array<string> {
    const rooms: Array<string> = [];

    if (this.rooms && this.rooms.length > 0) {
      this.rooms.forEach((room: Room) => {
        if (room.ref_number && rooms.indexOf(room.ref_number) < 0) {
          rooms.push(room.ref_number);
        }
      });
    }

    return rooms;
  }

  get roomsCount(): number {
    return this.rooms.length;
  }

  get roomsUniqueTypes() {
    const uniqueTypes: Array<Type> = [];

    this.rooms.forEach((room: Room) => {
      //  const index = uniqueTypes.findIndex(x => x.id === room.type_id);
      const index = uniqueTypes.findIndex((type: Type) => {
        if (type && type.id) {
          return type.id === room.type_id;
        }
      });

      if (index === -1) {
        uniqueTypes.push(room.type);
      }
    });

    return uniqueTypes;
  }

  get avgNetPriceEur() {
    return (
      sumBy(this.rooms, (room: Room) => {
        return Number(room.net_price_eur_avg);
      }) / this.rooms.length
    );
  }

  get roomsUnitedDates() {
    return unionBy(this.rooms, (room: Room) => {
      return room.check_in + room.check_out;
    });
  }
}
